/**
 * Phase 135B — StreamX Replay Player (compact + premium)
 * --------------------------------------------------------------------
 * Shared player for both authenticated `/livex/replays/:id` and public
 * `/livex/replays/public/:slug`. Renders:
 *   - Hero player area (real <video> when status=='ready', else processing
 *     placeholder showing the cinematic medallion).
 *   - Chapter timeline beneath the video — tap a chapter to seek to
 *     `clip_start_seconds`.
 *   - Highlight badges on a horizontal timeline strip.
 *   - Share + download actions.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Film, Play, ArrowLeft, Share2, Check, Sparkles, Clock, Eye,
  AlertTriangle, Calendar, Trophy, Crown, ListVideo,
} from 'lucide-react';
import api from '@/lib/api';
import ChapterClipActions from '@/components/livex/ChapterClipActions';
import StoryOfTheMatchTab from '@/components/livex/StoryOfTheMatchTab';
import '@/styles/livex.css';

function fmtTime(seconds) {
  const s = Math.max(0, Math.round(Number(seconds || 0)));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  if (m < 60) return `${m}:${ss}`;
  const h = Math.floor(m / 60);
  return `${h}:${String(m % 60).padStart(2, '0')}:${ss}`;
}
function fmtRelative(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
  } catch { return iso; }
}

export default function LivexReplayPlayer({ replay, mode = 'private', onShare }) {
  const videoRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState(null);

  // Phase 140B.2 Sprint 4 #2 — tab toggle between the existing
  // Chapters list and the new Story-of-the-Match auto-advancing
  // playlist. Default is 'chapters' to preserve the legacy entry
  // experience; the Story tab is a discoverable cinematic upgrade.
  const [activeTab, setActiveTab] = useState('chapters');

  // Phase 140B.2 Sprint 3 — enriched chapter data (joined with
  // livex_replay_clips so we know per-chapter clip_status / clip_url).
  // We fetch this lazily after the player mounts; until it lands, the
  // base `replay.chapters` array is rendered (so seek-to-moment never
  // waits on a network round-trip).
  const [clipMap, setClipMap] = useState({}); // highlight_id → { clip_status, clip_url, ... }

  useEffect(() => {
    let cancelled = false;
    async function loadHighlights() {
      if (!replay?.id) return;
      try {
        const res = await api.get(`/livex/replays/${replay.id}/highlights`);
        if (cancelled) return;
        const next = {};
        for (const h of (res?.data?.entries || [])) {
          next[h.id] = {
            clip_status: h.clip_status || 'none',
            clip_id: h.clip_id || null,
            clip_url: h.clip_url || null,
            clip_thumbnail_url: h.clip_thumbnail_url || null,
            clip_duration_seconds: h.clip_duration_seconds || null,
          };
        }
        setClipMap(next);
      } catch (_e) { /* best-effort enrichment */ }
    }
    loadHighlights();
    return () => { cancelled = true; };
  }, [replay?.id]);

  const handleClipUpdated = (highlightId, patch) => {
    setClipMap((prev) => ({ ...prev, [highlightId]: { ...(prev[highlightId] || {}), ...patch } }));
  };

  const status = replay.status || 'processing';
  const isReady = status === 'ready' && replay.mp4_url;
  const sportColors = {
    football: '#10b981', cricket: '#f59e0b', basketball: '#f97316',
    tennis: '#a855f7', hockey: '#3b82f6', badminton: '#06b6d4',
  };
  const accent = sportColors[(replay.sport || '').toLowerCase()] || '#FF5A1F';

  const handleSeek = (chapter) => {
    setActiveChapterId(chapter.id);
    if (videoRef.current && isReady) {
      try {
        videoRef.current.currentTime = chapter.clip_start_seconds;
        videoRef.current.play().catch(() => {});
      } catch { /* ignore */ }
    }
  };

  const handleShareClick = async () => {
    const shareUrl = `${window.location.origin}/livex/replays/public/${replay.public_slug}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
    onShare?.();
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid={`livex-replay-player-${mode}`}>
      {/* Top bar */}
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {mode === 'private' ? (
              <Link to="/streamx/replays" data-testid="livex-replay-back" className="flex-shrink-0 hover:opacity-80 transition-opacity">
                <ArrowLeft className="h-5 w-5 text-white" />
              </Link>
            ) : (
              <Link to="/streamx" data-testid="livex-replay-back" className="flex-shrink-0 hover:opacity-80 transition-opacity">
                <ArrowLeft className="h-5 w-5 text-white" />
              </Link>
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase flex items-center gap-1.5">
                <Film className="h-3 w-3" /> {mode === 'public' ? 'PUBLIC REPLAY' : 'REPLAY'}
              </div>
              <h1 className="text-base sm:text-lg font-black text-white tracking-tight truncate max-w-[60vw]">{replay.title}</h1>
            </div>
          </div>
          <button
            onClick={handleShareClick}
            data-testid="livex-replay-share"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white rounded-lg border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
          >
            {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            {copied ? 'Link copied' : 'Share replay'}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Player */}
        <div
          className="relative w-full rounded-2xl overflow-hidden border-2 border-[#22314F]"
          style={{
            aspectRatio: '16/9',
            background: `radial-gradient(circle at 30% 30%, ${accent}33, #050a14 75%), linear-gradient(135deg, #0c1a2e, #050a14)`,
          }}
          data-testid="livex-replay-player-area"
        >
          {isReady ? (
            <video
              ref={videoRef}
              data-testid="livex-replay-video"
              controls
              playsInline
              poster={replay.thumbnail_url || undefined}
              className="absolute inset-0 w-full h-full bg-black"
            >
              <source src={replay.mp4_url} type="video/mp4" />
            </video>
          ) : (
            <div className="absolute inset-0 grid place-items-center text-center p-6" data-testid="livex-replay-processing">
              <div>
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${accent}88)`, boxShadow: `0 14px 36px ${accent}55` }}
                >
                  {status === 'purged' ? <AlertTriangle className="h-7 w-7 text-white" /> : <Film className="h-7 w-7 text-white" />}
                </div>
                <h2 className="text-2xl font-black text-white mb-2">
                  {status === 'purged' ? 'Replay archived' : 'Replay is processing'}
                </h2>
                <p className="livex-body-text max-w-sm mx-auto" style={{ fontSize: '14px' }}>
                  {status === 'purged'
                    ? 'This replay has passed its retention window. Chapter metadata is preserved below for the academy archive.'
                    : 'Your broadcast ended successfully. Full video playback becomes available within minutes once the recording is encoded.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Metadata strip */}
        <div className="flex items-center gap-3 text-[12px] font-bold text-[#94A3B8] flex-wrap" data-testid="livex-replay-metadata">
          {replay.academy_name && (
            <span className="inline-flex items-center gap-1.5">
              <Trophy className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-white font-black">{replay.academy_name}</span>
            </span>
          )}
          <span className="text-[#22314F]">·</span>
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtRelative(replay.recorded_at)}</span>
          {replay.sport && (
            <>
              <span className="text-[#22314F]">·</span>
              <span className="capitalize">{replay.sport}</span>
            </>
          )}
          <span className="text-[#22314F]">·</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(replay.duration_seconds)}</span>
          <span className="text-[#22314F]">·</span>
          <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {replay.view_count || 0} views</span>
          {replay.retention_tier === 'pro' && (
            <>
              <span className="text-[#22314F]">·</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white"
                style={{ background: 'linear-gradient(135deg,#dc2626,#f97316)' }}>
                <Crown className="h-2.5 w-2.5" /> PRO
              </span>
            </>
          )}
        </div>

        {/* Phase 140B.2 Sprint 4 #2 — Chapters / Story tab toggle */}
        <div
          className="flex items-center gap-1.5 flex-wrap"
          data-testid="livex-replay-tab-toggle"
        >
          <button
            type="button"
            onClick={() => setActiveTab('chapters')}
            data-testid="livex-replay-tab-chapters"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider rounded-md border-2 transition-all ${
              activeTab === 'chapters'
                ? 'border-[#FF5A1F] text-white bg-[#FF5A1F]'
                : 'border-[#22314F] text-white/70 hover:border-[#FF5A1F]'
            }`}
          >
            <Sparkles className="h-3 w-3" /> Chapters · {(replay.chapters || []).length}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('story')}
            data-testid="livex-replay-tab-story"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider rounded-md border-2 transition-all ${
              activeTab === 'story'
                ? 'border-[#FF5A1F] text-white bg-[#FF5A1F]'
                : 'border-[#22314F] text-white/70 hover:border-[#FF5A1F]'
            }`}
          >
            <ListVideo className="h-3 w-3" /> Story of the Match
          </button>
        </div>

        {activeTab === 'story' ? (
          <StoryOfTheMatchTab streamId={replay.stream_id || replay.id} />
        ) : (replay.chapters || []).length > 0 ? (
          <section data-testid="livex-replay-chapters" className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[#FF5A1F]" />
              <h3 className="text-sm font-black text-white tracking-tight">
                {replay.chapters.length} {replay.chapters.length === 1 ? 'chapter' : 'chapters'} · jump to any moment
              </h3>
            </div>
            <ul className="space-y-2">
              {replay.chapters.map((c) => {
                const isActive = c.id === activeChapterId;
                return (
                  <li
                    key={c.id}
                    data-testid={`livex-replay-chapter-${c.id}`}
                    className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                      isActive ? 'border-[#FF5A1F] bg-[#1a0e10]' : 'border-[#22314F] bg-[#050a14] hover:border-[#FF5A1F]'
                    }`}
                    onClick={() => handleSeek(c)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSeek(c); }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg"
                        style={{ background: `${c.badge_color}22`, border: `1.5px solid ${c.badge_color}55` }}
                      >
                        <Play className="h-3.5 w-3.5" style={{ color: c.badge_color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-black capitalize text-white">{c.type.replace(/_/g, ' ')}</span>
                          <span className="text-[11px] font-bold text-[#94A3B8] tabular-nums">@ {fmtTime(c.t_seconds)}</span>
                          {/* Phase 140B.2 — Signal trigger pill. Manual ⭐ markers stay
                              unlabeled (they're the default). Auto-detected ones get
                              a short colour-coded badge so coaches can see WHY the
                              moment was captured (score change / crowd spike / viewer surge). */}
                          {c.auto_generated && (
                            <span
                              data-testid={`livex-replay-chapter-${c.id}-trigger`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.10em]"
                              style={{
                                background: 'rgba(255,90,31,0.14)',
                                color: '#FFB37A',
                                border: '1px solid rgba(255,90,31,0.40)',
                              }}
                            >
                              {c.trigger_type === 'score_change' && 'Auto · Score'}
                              {c.trigger_type === 'reaction_spike' && 'Auto · Crowd'}
                              {c.trigger_type === 'viewer_spike' && 'Auto · Viewers'}
                              {!['score_change','reaction_spike','viewer_spike'].includes(c.trigger_type) && 'Auto'}
                            </span>
                          )}
                        </div>
                        {c.note && <div className="text-[12px] text-[#cbd5e1] truncate mt-0.5">{c.note}</div>}
                        <div className="text-[10px] font-bold text-[#94A3B8] mt-1 tabular-nums">
                          clip: {fmtTime(c.clip_start_seconds)} — {fmtTime(c.clip_end_seconds)}
                        </div>
                        {/* Phase 140B.2 Sprint 3 — per-chapter clip CTA row.
                            Renders Play/Download when clip is ready, Generate
                            when status='none' (lazy spike triggers), Retry on
                            failure, busy indicator while processing. */}
                        <ChapterClipActions
                          chapter={{ ...c, ...(clipMap[c.id] || { clip_status: 'none' }) }}
                          onClipUpdated={handleClipUpdated}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : (
          <div className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-5 text-center" data-testid="livex-replay-no-chapters">
            <Sparkles className="h-7 w-7 mx-auto text-[#94A3B8] mb-2" />
            <p className="livex-body-text" style={{ fontSize: '13px' }}>
              No chapter markers were tapped during this broadcast. Next time, use <em className="text-[#FF5A1F]">Mark Highlight</em> during a stream to auto-generate replay chapters.
            </p>
          </div>
        )}

        {/* Footer attribution for public mode */}
        {mode === 'public' && (
          <div className="text-center pt-4 pb-2 text-[12px] font-bold text-[#94A3B8]">
            Broadcast on{' '}
            <Link to="/streamx" className="text-[#FF5A1F] hover:underline">StreamX by Out-Play</Link>
            {' '}— premium sports broadcasting for academies.
          </div>
        )}
      </div>
    </div>
  );
}
